import type { DeckCard, Suit } from "@/types/cards";

export interface CardProps {
  card: DeckCard;
  faceDown?: boolean;
  highlight?: boolean;
  selected?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  compact?: boolean;
}

// 花色标记 class
function suitClass(suit: Suit): string {
  if (suit === "红") return "suit-red";
  if (suit === "黑") return "suit-black";
  if (suit === "蓝") return "suit-blue";
  return "suit-none";
}

// 种类 → 主题 class + 水印字（与 cards/index.html 卡牌全集一致）
const KIND_THEME: Record<string, { cls: string; mark: string }> = {
  攻击牌: { cls: "kind-attack", mark: "攻" },
  效果牌: { cls: "kind-defense", mark: "御" },
  法术牌: { cls: "kind-magic", mark: "法" },
  摄魂牌: { cls: "kind-soul", mark: "魂" },
  功能牌: { cls: "kind-func", mark: "功" },
  公共法术牌: { cls: "kind-array", mark: "阵" },
  补给牌: { cls: "kind-potion", mark: "药" },
  装备牌: { cls: "kind-equip", mark: "器" },
};

// 悬停 tooltip：名称/花色/效果描述/响应链
function cardTooltip(card: DeckCard): string {
  const parts: string[] = [
    `${card.name} · ${card.kind}${card.subkind ? `（${card.subkind}）` : ""}`,
  ];
  if (card.suit || card.rank != null) {
    parts.push(`花色点数：${card.suit ? `${card.suit}色` : "—"} ${card.rank ?? ""}`);
  }
  if (card.effectText) parts.push(card.effectText);
  if (card.responds?.length) parts.push(`响应链：${card.responds.join(" / ")}`);
  return parts.join("\n");
}

export default function Card({
  card,
  faceDown,
  highlight,
  selected,
  disabled,
  onClick,
  compact,
}: CardProps) {
  const cls = [
    "card",
    compact ? "card--compact" : "",
    faceDown ? "card--back" : "",
    highlight ? "card--highlight" : "",
    selected ? "card--selected" : "",
    disabled ? "card--disabled" : "",
  ]
    .filter(Boolean)
    .join(" ");

  if (faceDown) {
    return (
      <div
        className={cls}
        onClick={onClick}
        role={onClick ? "button" : undefined}
      >
        <div className="card__back-pattern">
          <span className="card__back-seal">命</span>
        </div>
      </div>
    );
  }

  const theme = KIND_THEME[card.kind] ?? { cls: "kind-func", mark: "功" };

  return (
    <div
      className={`${cls} ${theme.cls}`}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      title={cardTooltip(card)}
    >
      <div className="card__suit-row">
        <span className={`card__suit ${suitClass(card.suit)}`}>
          {card.suit ?? "·"}
        </span>
        {card.rank != null && (
          <span className="card__rank">{card.rank}</span>
        )}
      </div>
      <div className="card__name">{card.name}</div>
      <div className="card__art">
        <span className="card__watermark">{theme.mark}</span>
        <i className="card__streak" />
        <i className="card__streak card__streak--2" />
      </div>
      <div className="card__kind-tag">{card.kind}</div>
    </div>
  );
}
